import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { getErrorMessage } from '../../utils/errorHandler';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Shield, Plus, Trash2, Save, AlertTriangle, Info } from 'lucide-react';
import {
  Alert,
  AlertDescription,
} from '../ui/alert';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminSecurity() {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState({
    captcha_disabled: false,
    ip_whitelist: []
  });
  const [newIp, setNewIp] = useState('');

  const fetchSecurityConfig = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/security-config`);
      setConfig({
        captcha_disabled: res.data.captcha_disabled || false,
        ip_whitelist: res.data.ip_whitelist || []
      });
    } catch (err) {
      console.error('Error fetching security config:', err);
      toast.error(getErrorMessage(err, 'Erreur de chargement'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSecurityConfig();
  }, [fetchSecurityConfig]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/security-config`, config);
      toast.success('Configuration sécurité sauvegardée');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Erreur de sauvegarde'));
    } finally {
      setSaving(false);
    }
  };

  const addIpToWhitelist = () => {
    const ip = newIp.trim();
    if (!ip) {
      toast.error('Veuillez entrer une adresse IP');
      return;
    }
    
    // Basic IP validation (IPv4 or CIDR notation)
    const ipv4Pattern = /^(\d{1,3}\.){3}\d{1,3}(\/\d{1,2})?$/;
    if (!ipv4Pattern.test(ip)) {
      toast.error('Format IP invalide (ex: 192.168.1.1 ou 192.168.1.0/24)');
      return;
    }
    
    if (config.ip_whitelist.includes(ip)) {
      toast.error('Cette IP est déjà dans la liste');
      return;
    }
    
    setConfig(prev => ({
      ...prev,
      ip_whitelist: [...prev.ip_whitelist, ip]
    }));
    setNewIp('');
  };

  const removeIpFromWhitelist = (ip) => {
    setConfig(prev => ({
      ...prev,
      ip_whitelist: prev.ip_whitelist.filter(i => i !== ip)
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="border border-border rounded-lg p-6 bg-card space-y-6" data-testid="admin-security">
      <div>
        <h2 className="text-xl font-semibold flex items-center gap-2">
          <Shield className="h-5 w-5" />
          Configuration Sécurité
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          Gérez les paramètres de sécurité de l'application
        </p>
      </div>

      {/* CAPTCHA Toggle */}
      <div className="space-y-4">
        <div className="flex items-center justify-between p-4 border border-border rounded-lg bg-muted/30">
          <div className="space-y-1">
            <Label className="text-base font-medium">Protection CAPTCHA (Turnstile)</Label>
            <p className="text-sm text-muted-foreground">
              Désactiver le CAPTCHA Cloudflare Turnstile sur la page de connexion
            </p>
          </div>
          <Switch
            checked={config.captcha_disabled}
            onCheckedChange={(checked) => setConfig(prev => ({ ...prev, captcha_disabled: checked }))}
            data-testid="captcha-toggle"
          />
        </div>

        {config.captcha_disabled && (
          <Alert variant="destructive" className="bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-800">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <strong>Attention :</strong> Désactiver le CAPTCHA expose l'application aux attaques par force brute. 
              Utilisez cette option uniquement en combinaison avec une liste blanche d'IPs ou pour des tests.
            </AlertDescription>
          </Alert>
        )}
      </div>

      {/* IP Whitelist */}
      <div className="space-y-4">
        <div>
          <Label className="text-base font-medium">Liste blanche d'IPs</Label>
          <p className="text-sm text-muted-foreground mt-1">
            Les IPs dans cette liste peuvent se connecter sans CAPTCHA. Supporte la notation CIDR (ex: 192.168.1.0/24).
          </p>
        </div>

        <Alert className="bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800">
          <Info className="h-4 w-4 text-blue-600" />
          <AlertDescription className="text-blue-700 dark:text-blue-400">
            Si la liste blanche est vide et le CAPTCHA est activé, tous les utilisateurs devront passer le CAPTCHA.
            Ajoutez les IPs de vos bureaux ou VPN pour un accès facilité.
          </AlertDescription>
        </Alert>

        <div className="flex gap-2">
          <Input
            placeholder="192.168.1.1 ou 10.0.0.0/8"
            value={newIp}
            onChange={(e) => setNewIp(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addIpToWhitelist()}
            className="flex-1"
            data-testid="ip-input"
          />
          <Button onClick={addIpToWhitelist} variant="outline" data-testid="add-ip-btn">
            <Plus className="h-4 w-4 mr-1" />
            Ajouter
          </Button>
        </div>

        {config.ip_whitelist.length > 0 ? (
          <div className="border border-border rounded-lg divide-y divide-border">
            {config.ip_whitelist.map((ip, index) => (
              <div key={index} className="flex items-center justify-between p-3 hover:bg-muted/50">
                <code className="text-sm font-mono bg-muted px-2 py-1 rounded">{ip}</code>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeIpFromWhitelist(ip)}
                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                  data-testid={`remove-ip-${index}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-6 text-muted-foreground border border-dashed border-border rounded-lg">
            <Shield className="h-8 w-8 mx-auto mb-2 opacity-30" />
            <p>Aucune IP dans la liste blanche</p>
          </div>
        )}
      </div>

      {/* Save Button */}
      <div className="flex gap-3 pt-4 border-t border-border">
        <Button
          onClick={handleSave}
          disabled={saving}
          data-testid="save-security-config-btn"
        >
          <Save className="h-4 w-4 mr-2" />
          {saving ? 'Sauvegarde...' : 'Sauvegarder'}
        </Button>
      </div>
    </div>
  );
}
